import { useNavigate, useLocation, useParams } from "react-router-dom"
import { FormI } from "./types"
import FormContainer from "./formContainer"
import { TrexNavigator } from "../../classes/navigator/navigator"
import ModalHeader from "../shared/modalHeader"
import styles from "./organizationMembershipAccessUpdate.module.scss"
import { ORGANIZATION_MEMBERSHIP_ACCESS_UPDATE_QRY } from "../../graphql/queries/OrganizationMembershipAccessUpdateQry"
import { useQuery } from "@apollo/client"
import { EmptyState } from "../shared/graphQlResponse"
import { ReactNode } from "react"
import { ErrorMessage, SyncLoaderComponent } from "../shared/graphQlResponse"

const OrganizationMembershipAccessUpdate = () => {
    // hooks
    const navigate = useNavigate()
    const location = useLocation()
    const { orgMembershipId } = useParams()
    if (!orgMembershipId) {
        throw new Error("No org membership id resolved from url params")
    }

    // queries
    const { data, loading, error } = useQuery(
        ORGANIZATION_MEMBERSHIP_ACCESS_UPDATE_QRY,
        {
            variables: { input: { ids: [orgMembershipId] } },
            // Fetching the the most recent wells & wellPermissions
            fetchPolicy: "network-only",
        }
    )

    // rendering
    let organizationMembershipAccessUpdateFormComponent: ReactNode

    if (loading) {
        organizationMembershipAccessUpdateFormComponent = (
            <SyncLoaderComponent outSideDivStyle={styles.loader} />
        )
    } else if (error) {
        organizationMembershipAccessUpdateFormComponent = (
            <ErrorMessage
                outSideDivStyle={styles.error}
                message="We had a problem while fetching this user's well access."
            />
        )
    } else if (
        !data?.organizationMembershipsByIDs.organizationMemberships ||
        data.organizationMembershipsByIDs.organizationMemberships.length === 0
    ) {
        organizationMembershipAccessUpdateFormComponent = (
            <EmptyState outSideDivStyle={styles.queryEmpty}>
                <> No user with this id was found.</>
            </EmptyState>
        )
    } else {
        if (!data.wells.nodes) {
            organizationMembershipAccessUpdateFormComponent = (
                <ErrorMessage
                    outSideDivStyle={styles.error}
                    message="We had a problem while fetching this user's well access."
                />
            )
        } else {
            // constants
            const orgMembership =
                data.organizationMembershipsByIDs.organizationMemberships[0]
            const fullName = orgMembership.subject.identity?.fullName || ""
            const { role: targetSubjectRole } = orgMembership
            const wells = data.wells.nodes

            // form
            const wellPermissionsSet = new Set(
                orgMembership.wellPermissions.map((wp) => wp.well.id)
            )

            const initialForm: FormI = {
                fullAccess: !orgMembership.managedAccess, // if managedAccess is false, then they have full access
                wellPermissions: wells.map((well) => ({
                    value: well.id,
                    label: well.name,
                    isSelected: wellPermissionsSet.has(well.id),
                    isDisabled: false,
                })),
                organizationMembershipId: orgMembershipId,
                targetSubjectId: orgMembership.subject.id,
            }

            organizationMembershipAccessUpdateFormComponent = (
                <>
                    <FormContainer
                        initialForm={initialForm}
                        targetSubjectName={fullName}
                        targetSubjectRole={targetSubjectRole}
                    />
                </>
            )
        }
    }

    return (
        <>
            <div className={styles.container}>
                <ModalHeader
                    title="Update Access"
                    trexNavigator={
                        new TrexNavigator(
                            {
                                navigateTo: -1,
                                locationKey: location.key,
                                pathName: location.pathname,
                            },
                            navigate
                        )
                    }
                />
                {organizationMembershipAccessUpdateFormComponent}
            </div>
        </>
    )
}

export default OrganizationMembershipAccessUpdate
